import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import useInstantPayEligible from '@/enterprise/components/InstantPay/useInstantPayEligible';
import { CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import localeRateToDomain from '@/utils/localeRateToDomain';
import { RateType, useOfferBuilderContext } from '../../OfferBuilderContext';
import {
  useValidateRate,
  ValidateRateResult,
  validateAndNormalizeRate,
  hasExpressAcceptForMarket,
  hasTrendingRateForMarket,
  getExistingRateFromConfig,
  getStrategyTitle,
  buildBulkRatePayload,
} from '../../utils';
import NameYourRateInput from '../NameYourRate/NameYourRateInput';

type StrategyType = 'expressAccept' | 'trendingRate' | 'existingRate';

interface BulkStrategyModalProps {
  takerMarkets: TakerMarket[];
  strategy: StrategyType;
  pricingData: CombinedPricingSuggestion | undefined;
  open: boolean;
  onClose: () => void;
}

const BulkStrategyModal = ({ takerMarkets, strategy, pricingData, open, onClose }: BulkStrategyModalProps) => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: { frequency, rateType: rateTypeFromContext, workingTakerMarkets },
  } = useOfferBuilderContext();
  const { trackEnterpriseEvent } = useReporting();
  const { validateRate } = useValidateRate();
  const { isInstantPayEligible } = useInstantPayEligible();
  const rateInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const rateTypeToUse = rateTypeFromContext ?? 'APR';

  const [internalRate, setInternalRate] = useState('');
  const [internalRateType, setInternalRateType] = useState<RateType>(rateTypeToUse);
  const [rateError, setRateError] = useState<ValidateRateResult | null>(null);

  // Count divisions with and without the strategy
  const { divisionsWithStrategy, divisionsWithoutStrategy } = useMemo(() => {
    const withStrategy: TakerMarket[] = [];
    const withoutStrategy: TakerMarket[] = [];

    takerMarkets.forEach((tm) => {
      let hasStrategy = false;

      if (strategy === 'expressAccept') {
        hasStrategy = hasExpressAcceptForMarket(tm, pricingData, rateTypeToUse, isInstantPayEligible);
      } else if (strategy === 'trendingRate') {
        hasStrategy = hasTrendingRateForMarket(tm, pricingData, rateTypeToUse, frequency);
      } else if (strategy === 'existingRate') {
        hasStrategy = getExistingRateFromConfig(tm, rateTypeToUse) !== null;
      }

      if (hasStrategy) {
        withStrategy.push(tm);
      } else {
        withoutStrategy.push(tm);
      }
    });

    return {
      divisionsWithStrategy: withStrategy,
      divisionsWithoutStrategy: withoutStrategy,
    };
  }, [takerMarkets, strategy, pricingData, rateTypeToUse, frequency, isInstantPayEligible]);

  // Initialize state when modal opens
  useEffect(() => {
    if (open && !initializedRef.current) {
      setInternalRate('');
      setInternalRateType(rateTypeToUse);
      setRateError(null);
      initializedRef.current = true;
    } else if (!open) {
      initializedRef.current = false;
    }
  }, [open, rateTypeToUse]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (rateInputRef.current) {
          rateInputRef.current.focus();
        }
      });
    }
  }, [open]);

  const handleRateChange = useCallback((value: string) => {
    setInternalRate(value);
    setRateError(null);
  }, []);

  const handleRateBlur = useCallback(() => {
    const domainRate = localeRateToDomain(internalRate) ?? '';

    if (domainRate && internalRateType) {
      const validationResult = validateRate(domainRate, internalRateType);

      if (!validationResult.valid) {
        setRateError(validationResult);
      }
    }
  }, [internalRate, internalRateType, validateRate]);

  const handleRateTypeChange = useCallback((value: RateType) => {
    setInternalRateType(value);
    setInternalRate('');
  }, []);

  const handleApply = () => {
    // Validate rate only if there are divisions without strategy
    let normalizedRate: number | undefined;
    if (divisionsWithoutStrategy.length > 0) {
      const domainRate = localeRateToDomain(internalRate) ?? '';
      const result = validateAndNormalizeRate(domainRate, internalRateType, validateRate, t);

      if (!result.success) {
        if (result.errorMessage) {
          setRateError({ valid: false, errorMessage: result.errorMessage });
        }

        return;
      }

      normalizedRate = result.normalizedRate;
    }

    setRateError(null);

    // Build payload - try strategy rate first, fallback to custom rate if needed
    let payload = buildBulkRatePayload(
      takerMarkets,
      strategy,
      internalRateType,
      frequency,
      pricingData,
      workingTakerMarkets,
      undefined,
      false,
      isInstantPayEligible
    );

    // If strategy rate is not available for some markets and user provided a custom rate, apply it
    if (normalizedRate !== undefined && payload.length < takerMarkets.length) {
      const strategyMarketKeys = new Set(payload.map((p) => p.takerMarketKey));
      const remainingMarkets = takerMarkets.filter(
        (tm) => !strategyMarketKeys.has(`${tm.marketUuid}-${tm.takerDivisionUuid}`)
      );

      if (remainingMarkets.length > 0) {
        const customPayload = buildBulkRatePayload(
          remainingMarkets,
          'nameYourRate',
          internalRateType,
          frequency,
          pricingData,
          workingTakerMarkets,
          normalizedRate,
          false
        );
        payload = [...payload, ...customPayload];
      }
    }

    if (payload.length === 0) {
      return;
    }

    dispatch({
      type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
      payload,
    });

    dispatch({
      type: 'SET_RATE_TYPE',
      payload: internalRateType,
    });

    trackEnterpriseEvent('offer-builder::apply-to-all::strategy-selected', {
      strategy,
      rateType: internalRateType,
      totalMarkets: takerMarkets.length,
      marketsUpdated: payload.length,
      frequency,
      divisionsWithStrategy: divisionsWithStrategy.length,
      divisionsWithoutStrategy: divisionsWithoutStrategy.length,
    });

    onClose();
  };

  const handleClose = useCallback(() => {
    trackEnterpriseEvent('offer-builder::bulk-name-your-rate-modal::closed', {
      strategy,
      hadInput: internalRate.trim() !== '',
    });
    onClose();
  }, [internalRate, onClose, trackEnterpriseEvent, strategy]);

  return (
    <Modal onClose={handleClose} open={open} size="sm">
      <ModalTitleInverse>
        {getStrategyTitle(strategy, t)} {t('builder.applyAll')}
      </ModalTitleInverse>
      <ModalContent className="pt-0">
        {divisionsWithStrategy.length > 0 ? (
          <>
            <Alert
              description={t('builder.strategySelectedForDivisions', {
                strategy: getStrategyTitle(strategy, t),
                count: divisionsWithStrategy.length,
              })}
              full
              type="success"
              variant="outlined"
            />
            <div className="my-4 text-sm">
              {t('builder.setRateForDivisionsWithout', {
                strategy: getStrategyTitle(strategy, t),
                count: divisionsWithoutStrategy.length,
              })}
            </div>
            <NameYourRateInput
              internalRate={internalRate}
              internalRateType={internalRateType}
              rateError={rateError}
              rateTypeError={null}
              onRateChange={handleRateChange}
              onRateBlur={handleRateBlur}
              onRateTypeChange={handleRateTypeChange}
              rateInputRef={rateInputRef}
            />
          </>
        ) : null}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          {t('core.back')}
        </Button>
        <Button variant="primary" onClick={handleApply}>
          {t('core.apply')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default BulkStrategyModal;
